import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import QRCode from "qrcode";
import { toast } from "react-toastify";

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

const convertToBase64 = (url) => {
  return new Promise((resolve) => {
    if (!url) return resolve("/assets/images/idcard/student.png");
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;
      canvas.getContext("2d").drawImage(img, 0, 0);
      resolve(canvas.toDataURL("image/png"));
    };
    img.onerror = () => resolve("/assets/images/idcard/student.png");
    img.src = url;
  });
};

export const capitalizeWords = (str = "") =>
  str.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());

const IdCard = async (data) => {
  if (!data || (Array.isArray(data) && data.length === 0)) {
    toast.error("Missing student details", { position: "top-right" });
    return;
  }

  const students = Array.isArray(data) ? data : [data];
  const targetWidth = 54;
  const targetHeight = 86;
  const pdf = new jsPDF("p", "mm", [targetWidth, targetHeight]);

  for (let i = 0; i < students.length; i++) {
    const student = students[i];
    if (!student?.studentDetails) continue;

    try {
      const admissionNo = student.admission_no;
      if (!admissionNo) continue;

      // preload images
      const [studentImage, qrDataURL] = await Promise.all([
        convertToBase64(student.studentDetails.studentImage),
        QRCode.toDataURL(admissionNo),
      ]);

      // container setup
      const container = document.createElement("div");
      Object.assign(container.style, {
        position: "fixed",
        top: "-10000px",
        left: "-10000px",
        width: "210mm",
        height: "335mm",
        background: `#ff9201 url("/assets/images/idcard/bg.jpg") center/cover no-repeat`,
        zIndex: "-1",
      });

      container.innerHTML = `<div class="pb-2 px-5 mx-3 mt-4 position-relative z-1">
      <div class="d-flex align-items-center">
          <img
            src="/assets/images/icon.png"
            width="160"
            height="160"
          />
          <div class="ms-5">
            <h1 class="id-text-primary display-1 fw-bold mb-2 lh-1 ls-sm eczar-font">आचार्यकुलम्</h1>
            <p class="id-text-primary id-text-medium m-0 fw-bold lh-sm eczar-font"><i class="bi bi-geo-alt-fill me-2"></i> Tata Road, Namkum, Ranchi - 834010</p>
            <p class="id-text-primary id-text-medium m-0 fw-bold lh-1 eczar-font"><i class="bi bi-telephone-fill me-2"></i> Phone No. (+91) 6287842460 / 64</p>
          </div>
      </div>
    </div>

    <div class="mx-3">
      <div class="px-5 py-4 mt-7 mb-3">
        <div class="row g-0">
          <div class="col-5">
            <div
              style="
                width: 260px;
                height: 260px;
                background-image: url('${studentImage}');
                background-size: cover;
                background-position: center;
                background-repeat: no-repeat;
                border-radius: 100%;
                border: 9px solid white;
              "
            ></div>
          </div>
          <div class="col-7 d-flex flex-column justify-content-center">
            <div class="me-2 ms-2 mt-n3">
              <p class="text-dark id-text-normal m-0 fw-medium lh-sm eczar-font">Admission No : <span class="fw-bold">${
                student?.admission_no
              }</span></p>
              <p class="text-dark id-text-normal m-0 fw-medium lh-sm eczar-font">Class / Sec : <span class="fw-bold">${
                student?.currentClass
              } - ${student?.studentDetails?.currentSection}</span></p>
              <p class="text-dark id-text-normal m-0 fw-medium lh-sm eczar-font">Roll No : <span class="fw-bold">${
                student?.studentDetails?.rollNo
              }</span></p>
              <p class="text-dark id-text-normal m-0 fw-medium lh-sm eczar-font">DOB : <span class="fw-bold">${new Date(
                student?.studentDetails?.dateOfBirth
              ).toLocaleDateString("en-GB")}</span></p>
              <p class="text-dark id-text-normal m-0 fw-medium lh-sm eczar-font">House : <span class="fw-bold">${
                student?.studentDetails?.house
              }</span></p>
              <p class="text-dark id-text-normal m-0 fw-medium lh-sm eczar-font">Blood Group : <span class="fw-bold">${
                student?.studentDetails?.bloodGroup
              }</span></p>
              <p class="text-dark id-text-normal m-0 fw-medium mt-3 eczar-font">Session : <span class="fw-bold">${
                student?.session
              }</span></p>
            </div>
            <div class="d-flex justify-content-end mt-n4">
              <img src="${qrDataURL}"
                width="130"
                height="130"
                class="rounded-4 bg-white border border-secondary p-1"
              />
            </div>
          </div>
        </div>
      </div>

      <div class="px-5 mt-n10">
        <div class="row g-0">
          <div class="col-12">
            <div class="col-8">
              <p class="text-dark display-4 m-0 text-uppercase eczar-font mb-3 pb-1 lh-1 pt-0"><b>${
                student?.studentDetails?.name
              }</b></p>
            </div>
            <p class="text-dark h2 m-0 fw-medium lh-1 eczar-font">Father's Name</p>
            <p class="text-dark id-text-large m-0 fw-semibold mb-1 eczar-font"><i class="bi bi-person-fill id-icon-primary"></i> ${capitalizeWords(
              student?.parentDetails?.[0]?.name || ""
            )}</p>
            <p class="text-dark h2 m-0 fw-medium lh-1 mt-2 eczar-font">Mother's Name</p>
            <p class="text-dark id-text-large m-0 fw-semibold mb-1 eczar-font"><i class="bi bi-person-fill id-icon-primary"></i> ${capitalizeWords(
              student?.parentDetails?.[1]?.name || ""
            )}</p>
            <p class="text-dark h2 m-0 fw-medium lh-1 mt-2 eczar-font">Address :</p>
            <p class="text-dark id-text-large m-0 fw-semibold mb-1 lh-1 eczar-font line-clamp-2"><i class="bi bi-geo-alt-fill id-icon-primary"></i> ${capitalizeWords(
              student?.presentAddress?.address || ""
            )}</p>
            <p class="text-dark h2 m-0 fw-medium lh-1 mt-2 eczar-font">Phone No :</p>
            <p class="text-dark id-text-large m-0 fw-semibold mb-1 eczar-font"><i class="bi bi-telephone-fill id-icon-primary"></i> (+91) ${
              student?.studentDetails?.phoneNo
            }</p>
            <p class="text-dark h2 m-0 fw-medium lh-1 mt-2 eczar-font">Bus Stoppage :</p>
            <p class="text-dark id-text-large m-0 fw-semibold mb-1 eczar-font"><i class="bi bi-bus-front-fill id-icon-primary"></i> ${capitalizeWords(
              student?.transportFacility?.busStop || "NONE"
            )}</p>
            <div class="d-flex"><p class="text-dark h2 m-0 fw-semibold me-3 eczar-font"> ${
              student.transportFacility?.busNumber ? "Bus No" : "Walker"
            } :</p> <div class="id-circle">
                  <div class="id-content">
                    <b class="text-dark id-text-large m-0 fw-bold eczar-font">
                      ${
                        student.transportFacility.busNumber ||
                        '<i class="bi bi-person-walking"></i>'
                      }
                    </b>
                  </div>
                </div>
            </div>
          </div>
        </div>
        
        <div class="float-end mt-n8">
          <div class="me-3 text-center">
            <img
              src="/assets/images/idcard/director.png"
              width="190"
              height="70"
              class="object-fit-contain"
            />
            <p class="text-dark h1 m-0 fw-bold lh-sm eczar-font">Issuing Authority</p>
          </div>
        </div>
      </div>
    </div>`;

      document.body.appendChild(container);
      await sleep(100);

      const canvas = await html2canvas(container, {
        scale: 1.5,
        useCORS: true,
        logging: false,
        backgroundColor: null,
      });

      container.remove();

      const imgData = canvas.toDataURL("image/png");
      const ratio = Math.min(
        targetWidth / canvas.width,
        targetHeight / canvas.height
      );
      const imgWidth = canvas.width * ratio;
      const imgHeight = canvas.height * ratio;
      const x = (targetWidth - imgWidth) / 2;
      const y = (targetHeight - imgHeight) / 2;

      if (i > 0) pdf.addPage();
      pdf.addImage(imgData, "PNG", x, y, imgWidth, imgHeight);
      await sleep(200);
    } catch (err) {
      console.error("Error rendering student", students[i], err);
      toast.error(
        `Error rendering ${student?.studentDetails?.name || "Unknown"}`
      );
    }
  }

  try {
    if (pdf.saveAsync) await pdf.saveAsync("IDCard.pdf");
    else pdf.save("IDCard.pdf");
  } catch (e) {
    toast.error("Failed to save PDF");
  }
};

export default IdCard;
